import React, { useState, useMemo } from "react";

/**
 * Parses any incoming video URL, embed code, or direct video file into a standardized format.
 * Supports: YouTube, Vimeo, Loom, Google Drive, Muvi, Dailymotion, Wistia, and direct HTML5 video files.
 */
export const parseVideoUrl = (rawInput) => {
  if (!rawInput || typeof rawInput !== "string") {
    return { type: "empty", embedUrl: "", rawUrl: "" };
  }

  let cleanInput = rawInput.trim();

  // 1. If an entire <iframe> embed code is passed, extract the src
  if (cleanInput.includes("<iframe") && cleanInput.includes("src=")) {
    const srcMatch = cleanInput.match(/src=["']([^"']+)["']/i);
    if (srcMatch && srcMatch[1]) {
      cleanInput = srcMatch[1];
    }
  }

  // Check for direct video files (.mp4, .webm, .ogg, .mov, .m4v, .m3u8)
  const isDirectVideo = /\.(mp4|webm|ogg|ogv|mov|m4v|m3u8)(\?.*)?$/i.test(cleanInput);
  if (isDirectVideo) {
    return {
      type: "html5",
      embedUrl: cleanInput,
      rawUrl: cleanInput,
      provider: "Direct Video",
    };
  }

  // 2. YouTube
  // Matches: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID, youtube.com/shorts/ID, etc.
  const ytMatch = cleanInput.match(
    /(?:https?:\/\/)?(?:www\.|m\.|music\.)?(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|v\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/i
  );
  if (ytMatch && ytMatch[1]) {
    const videoId = ytMatch[1];
    // Check for start time (e.g. t=1m20s or t=80)
    let startParam = "";
    const timeMatch = cleanInput.match(/[?&]t=(\d+h)?(\d+m)?(\d+s?|\d+)/i);
    if (timeMatch) {
      let seconds = 0;
      if (timeMatch[1]) seconds += parseInt(timeMatch[1], 10) * 3600;
      if (timeMatch[2]) seconds += parseInt(timeMatch[2], 10) * 60;
      if (timeMatch[3]) seconds += parseInt(timeMatch[3], 10);
      if (seconds > 0) startParam = `&start=${seconds}`;
    }

    return {
      type: "iframe",
      embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1&playsinline=1${startParam}`,
      rawUrl: `https://www.youtube.com/watch?v=${videoId}`,
      provider: "YouTube",
    };
  }

  // 3. Vimeo
  // Matches: vimeo.com/123456789, player.vimeo.com/video/123456789, vimeo.com/channels/staffpicks/123456789
  const vimeoMatch = cleanInput.match(
    /(?:https?:\/\/)?(?:www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)/i
  );
  if (vimeoMatch && vimeoMatch[1]) {
    const videoId = vimeoMatch[1];
    return {
      type: "iframe",
      embedUrl: `https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0&portrait=0&dnt=1`,
      rawUrl: `https://vimeo.com/${videoId}`,
      provider: "Vimeo",
    };
  }

  // 4. Loom
  // Matches: loom.com/share/ID, loom.com/embed/ID
  const loomMatch = cleanInput.match(/(?:https?:\/\/)?(?:www\.)?loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/i);
  if (loomMatch && loomMatch[1]) {
    const videoId = loomMatch[1];
    return {
      type: "iframe",
      embedUrl: `https://www.loom.com/embed/${videoId}?autoplay=1`,
      rawUrl: `https://www.loom.com/share/${videoId}`,
      provider: "Loom",
    };
  }

  // 5. Google Drive / Docs Video Preview
  const gdriveMatch = cleanInput.match(
    /(?:https?:\/\/)?(?:drive|docs)\.google\.com\/(?:file\/d\/|open\?id=)([a-zA-Z0-9_-]+)/i
  );
  if (gdriveMatch && gdriveMatch[1]) {
    const fileId = gdriveMatch[1];
    return {
      type: "iframe",
      embedUrl: `https://drive.google.com/file/d/${fileId}/preview`,
      rawUrl: `https://drive.google.com/file/d/${fileId}/view`,
      provider: "Google Drive",
    };
  }

  // 6. Dailymotion
  const dmMatch = cleanInput.match(/(?:https?:\/\/)?(?:www\.)?dailymotion\.com\/(?:video|embed\/video)\/([a-zA-Z0-9]+)/i);
  if (dmMatch && dmMatch[1]) {
    const videoId = dmMatch[1];
    return {
      type: "iframe",
      embedUrl: `https://www.dailymotion.com/embed/video/${videoId}?autoplay=1`,
      rawUrl: `https://www.dailymotion.com/video/${videoId}`,
      provider: "Dailymotion",
    };
  }

  // 7. Wistia
  const wistiaMatch = cleanInput.match(/(?:https?:\/\/)?(?:[a-zA-Z0-9-]+\.)?(?:wistia\.com|wistia\.net)\/(?:medias|embed\/iframe)\/([a-zA-Z0-9]+)/i);
  if (wistiaMatch && wistiaMatch[1]) {
    const videoId = wistiaMatch[1];
    return {
      type: "iframe",
      embedUrl: `https://fast.wistia.net/embed/iframe/${videoId}?autoPlay=true`,
      rawUrl: `https://fast.wistia.net/embed/iframe/${videoId}`,
      provider: "Wistia",
    };
  }

  // 8. Muvi & Other Generic HTTPS Embeds/Players
  // If the link is already a web URL, use it directly as embed src
  if (/^https?:\/\//i.test(cleanInput)) {
    const isMuvi = /muvi\.com/i.test(cleanInput);
    return {
      type: "iframe",
      embedUrl: cleanInput,
      rawUrl: cleanInput,
      provider: isMuvi ? "Muvi" : "Web Video",
    };
  }

  // Default fallback
  return {
    type: "iframe",
    embedUrl: cleanInput,
    rawUrl: cleanInput,
    provider: "Video",
  };
};

/**
 * Universal Video Player Component
 * @param {string} src - The video URL, iframe code, or direct media path
 * @param {string} title - Accessible title for iframe / player
 * @param {string} height - Optional fixed height (defaults to responsive 16:9 ratio)
 * @param {boolean} showExternalLink - Whether to display "Open in New Tab ↗" helper bar
 * @param {string} className - Additional CSS class name
 */
const VideoPlayer = ({
  src,
  title = "Video Player",
  height,
  showExternalLink = true,
  className = "",
  style = {},
}) => {
  const [loadError, setLoadError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const videoInfo = useMemo(() => parseVideoUrl(src), [src]);

  if (!src || videoInfo.type === "empty" || !videoInfo.embedUrl) {
    return (
      <div
        className={`p-4 text-center rounded text-muted ${className}`}
        style={{
          background: "#F1F5F9",
          border: "1px dashed #CBD5E1",
          minHeight: "200px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          ...style,
        }}
      >
        <span style={{ fontSize: "28px", marginBottom: "8px" }}>📹</span>
        <div className="fw-semibold text-secondary">No video source provided</div>
        <small className="text-muted">The video link for this resource is currently unavailable.</small>
      </div>
    );
  }

  const containerStyle = {
    position: "relative",
    width: "100%",
    background: "#0B0F19",
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    ...(height
      ? { height: height }
      : { paddingBottom: "56.25%", height: 0 }), // Responsive 16:9 aspect ratio
    ...style,
  };

  const mediaStyle = {
    position: height ? "static" : "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: height || "100%",
    border: "none",
    borderRadius: "10px",
  };

  return (
    <div className={`dt-video-player-wrapper ${className}`}>
      <div style={containerStyle}>
        {/* Loading Spinner for Iframes */}
        {videoInfo.type === "iframe" && !isLoaded && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#0B0F19",
              color: "#94A3B8",
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            <div className="text-center">
              <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
                <span className="visually-hidden">Loading video...</span>
              </div>
              <div style={{ fontSize: "12px" }}>Loading video player...</div>
            </div>
          </div>
        )}

        {/* HTML5 Native Video Tag */}
        {videoInfo.type === "html5" ? (
          <video
            controls
            autoPlay
            playsInline
            style={{ ...mediaStyle, objectFit: "contain", background: "#000" }}
            onError={() => setLoadError(true)}
            onLoadedData={() => setIsLoaded(true)}
          >
            <source src={videoInfo.embedUrl} />
            Your browser does not support HTML5 video playback.
          </video>
        ) : (
          /* Third Party / Embed Iframe */
          <iframe
            src={videoInfo.embedUrl}
            title={title}
            style={{ ...mediaStyle, background: "#0B0F19" }}
            frameBorder="0"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
            referrerPolicy="no-referrer-when-downgrade"
            onLoad={() => setIsLoaded(true)}
            onError={() => setLoadError(true)}
          />
        )}
      </div>

      {/* External Link & Fallback Controls */}
      {showExternalLink && videoInfo.rawUrl && (
        <div
          className="d-flex align-items-center justify-content-between px-2 py-2 mt-2 rounded"
          style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", fontSize: "12px" }}
        >
          <div className="d-flex align-items-center gap-2 text-muted">
            <span
              className="badge"
              style={{
                background: "var(--dt-navy, #181059)",
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: "11px",
              }}
            >
              {videoInfo.provider}
            </span>
            <span className="d-none d-sm-inline">Having trouble loading this video?</span>
          </div>
          <a
            href={videoInfo.rawUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 py-1 px-2"
            style={{ fontSize: "12px", fontWeight: 600, textDecoration: "none" }}
          >
            <span>Open in New Tab</span>
            <span style={{ fontSize: "14px", lineHeight: "1" }}>↗</span>
          </a>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
